import { AppBrandLogoComponent } from '../../shared/ui/brand-logo/brand-logo.component';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CompanyContextService, SidebarStateService, TenantService } from '../../core';
import type { Vertical } from '../../core/enums/verticals';
import { CompanyVerticalModalComponent } from '../../features/company-context/components/company-vertical-modal/company-vertical-modal.component';
import { CompanyStore } from '../../features/company-context/store/company.store';
import { StatusMessageBannerComponent } from '../../features/billing/components/status-message-banner/status-message-banner.component';
import { TrialBannerComponent } from '../../features/billing/components/trial-banner/trial-banner.component';
import { ChecklistCompletionModalComponent } from '../../features/onboarding/components/onboarding-checklist/checklist-completion-modal.component';
import { OnboardingTourComponent } from '../../features/onboarding/components/onboarding-tour/onboarding-tour.component';
import { TPipe } from '../../shared/pipes';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    AppBrandLogoComponent,
    RouterOutlet,
    RouterLink,
    LucideAngularModule,
    TPipe,
    SidebarComponent,
    TopbarComponent,
    TrialBannerComponent,
    StatusMessageBannerComponent,
    OnboardingTourComponent,
    ChecklistCompletionModalComponent,
    CompanyVerticalModalComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.css',
})
export class MainLayoutComponent {
  private readonly router = inject(Router);
  private readonly sidebarState = inject(SidebarStateService);
  private readonly tenant = inject(TenantService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly companyStore = inject(CompanyStore);

  readonly isCollapsed = this.sidebarState.isCollapsed;

  private readonly currentUrl = signal<string>(this.router.url);

  readonly dashboardRoute = computed<string>(() => {
    void this.currentUrl();
    const m = this.router.url.match(/\/c\/([^/?#]+)/);
    const key = m?.[1] ?? this.tenant.companyKey() ?? '';
    return key ? `/c/${key}/dashboard` : '/';
  });

  /**
   * Sessão-only: cliente clicou "depois" e modal não deve reabrir nesta sessão.
   * Reseta automaticamente em troca de Company (effect abaixo).
   */
  private readonly dismissedThisSession = signal(false);
  private lastVerticalCompanyId: string | null = null;

  readonly verticalModalActiveCompanyId = computed(
    () => this.companyContext.activeCompany()?.id ?? null
  );

  /**
   * Modal só aparece com:
   * - Company ativa
   * - `vertical === null` EXPLÍCITO (undefined = DTO não retornou o campo, não cobrar)
   * - usuário Owner (quem realmente pode salvar via PUT)
   * - não-dispensado nesta sessão
   */
  readonly verticalModalVisible = computed<boolean>(() => {
    const c = this.companyContext.activeCompany();
    if (!c) return false;
    if (c.vertical !== null) return false;
    if (!this.companyContext.isCompanyOwner()) return false;
    return !this.dismissedThisSession();
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
      });

    // Reseta dismiss quando troca de Company — sem isso, "depois" da Company A
    // vazaria pra Company B (também com vertical null).
    effect(() => {
      const id = this.verticalModalActiveCompanyId();
      if (id !== this.lastVerticalCompanyId) {
        this.lastVerticalCompanyId = id;
        this.dismissedThisSession.set(false);
      }
    });
  }

  toggleSidebar(): void {
    this.sidebarState.toggle();
  }

  protected onVerticalModalClosed(): void {
    this.dismissedThisSession.set(true);
  }

  protected onVerticalSelected(vertical: Vertical): void {
    const id = this.verticalModalActiveCompanyId();
    if (!id) return;
    this.companyStore.applyCompanyVertical(id, vertical);
  }
}
